/* ============================================================================
   Metas — cursos planejados por ano, com progresso e acompanhamento.
   ============================================================================ */
import { useState, useEffect } from 'react';
import api from '../../services/api';
import AdminLayout from '../components/AdminLayout';
import PageHeader from '../components/PageHeader';
import { Loading, EmptyState } from '../components/ui';
import { Field, TextInput, Select } from '../components/Field';
import { Modal, confirm } from '../components/Modal';
import { toast } from '../components/Toast';
import { IconPlus, IconGoals, IconEdit, IconTrash, IconX, IconCheck } from '../components/icons';
import './Goals.css';

const TYPE_LABELS = { courses_per_year: 'Cursos lançados por ano' };
const emptyForm = () => ({ type: 'courses_per_year', year: new Date().getFullYear(), target: 1, courses: [] });

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [draft, setDraft] = useState({ name: '', isFree: false, planIds: [] });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [g, p] = await Promise.all([api.get('/admin/goals', true), api.get('/admin/plans', true)]);
      if (g.success) setGoals(g.data || []);
      if (p.success) setPlans(p.data || []);
    } catch { toast.error('Erro ao carregar metas.'); }
    finally { setLoading(false); }
  };

  /* ── Form (criar/editar) ── */
  const openNew = () => { setEditingId(null); setForm(emptyForm()); setDraft({ name: '', isFree: false, planIds: [] }); setShowForm(true); };
  const openEdit = (goal) => { setEditingId(goal.id); setForm({ type: goal.type, year: goal.year, target: goal.target, courses: goal.courses || [] }); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setEditingId(null); };

  const addDraftCourse = () => {
    if (!draft.name.trim()) return;
    setForm((f) => ({ ...f, courses: [...f.courses, { name: draft.name.trim(), isFree: draft.isFree, completed: false, planIds: draft.isFree ? [] : draft.planIds }] }));
    setDraft({ name: '', isFree: false, planIds: [] });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/goals/${editingId}`, form, true);
      else await api.post('/admin/goals', form, true);
      toast.success(editingId ? 'Meta atualizada.' : 'Meta criada.');
      closeForm();
      load();
    } catch (err) { toast.error(err.message || 'Erro ao salvar meta.'); }
  };

  const handleDelete = async (goal) => {
    const ok = await confirm({ title: 'Deletar meta', message: 'Tem certeza que deseja deletar esta meta?', confirmLabel: 'Deletar', danger: true });
    if (!ok) return;
    try {
      await api.delete(`/admin/goals/${goal.id}`, true);
      setGoals((prev) => prev.filter((g) => g.id !== goal.id));
      toast.success('Meta deletada.');
    } catch (err) { toast.error(err.message || 'Erro ao deletar meta.'); }
  };

  /* ── Edição de curso dentro do card (salva na hora) ── */
  const patchCourse = async (goal, index, patch) => {
    const courses = goal.courses.map((c, i) => i === index ? { ...c, ...patch } : c);
    const optimistic = { ...goal, courses };
    setGoals((prev) => prev.map((g) => g.id === goal.id ? optimistic : g));
    try {
      await api.put(`/admin/goals/${goal.id}`, optimistic, true);
    } catch {
      toast.error('Erro ao atualizar curso.');
      load();
    }
  };

  const toggleDraftPlan = (id) => setDraft((d) => ({ ...d, planIds: d.planIds.includes(id) ? d.planIds.filter((x) => x !== id) : [...d.planIds, id] }));
  const progress = (goal) => Math.min(Math.round(((goal.courses || []).filter((c) => c.completed).length / (goal.target || 1)) * 100), 100);

  return (
    <AdminLayout>
      <PageHeader
        title="Metas"
        subtitle="Defina e acompanhe as metas de lançamento de cursos"
        action={<button className="mg-btn mg-btn-primary" onClick={openNew}><IconPlus /> Nova meta</button>}
      />

      {loading ? <Loading /> : (
        <>
          {goals.length === 0 ? (
            <EmptyState icon={<IconGoals size={26} />} title="Nenhuma meta cadastrada"
              action={<button className="mg-btn mg-btn-primary" onClick={openNew}><IconPlus /> Criar primeira meta</button>}>
              Estabeleça quantos cursos planeja lançar por ano.
            </EmptyState>
          ) : (
            <div className="goals-grid">
              {goals.map((goal) => {
                const pct = progress(goal);
                const done = (goal.courses || []).filter((c) => c.completed).length;
                return (
                  <div key={goal.id} className="goals-card">
                    <div className="goals-card-head">
                      <span className="goals-type">{TYPE_LABELS[goal.type] || goal.type}</span>
                      <span className="goals-year">{goal.year}</span>
                    </div>

                    <div className="goals-progress">
                      <div className="goals-pie" style={{ background: `conic-gradient(var(--mg-coffee) ${pct * 3.6}deg, var(--mg-surface-3) ${pct * 3.6}deg)` }}>
                        <span>{pct}%</span>
                      </div>
                      <div className="goals-progress-info">
                        <span className="goals-count">{done}<em>/{goal.target}</em></span>
                        <span className="goals-count-lbl">cursos lançados</span>
                      </div>
                    </div>

                    {(goal.courses || []).length > 0 && (
                      <div className="goals-courses">
                        {goal.courses.map((course, i) => (
                          <div key={i} className={`goals-course${course.completed ? ' is-done' : ''}`}>
                            <button className="goals-check" onClick={() => patchCourse(goal, i, { completed: !course.completed })} title={course.completed ? 'Concluído' : 'Marcar como concluído'}>
                              {course.completed && <IconCheck size={13} />}
                            </button>
                            <span className="goals-course-name">{course.name}</span>
                            <input
                              type="date"
                              className="goals-date"
                              value={course.releaseDate || ''}
                              onChange={(e) => patchCourse(goal, i, { releaseDate: e.target.value })}
                            />
                            <button
                              className={`goals-tag${course.isFree ? ' is-free' : ''}`}
                              onClick={() => patchCourse(goal, i, { isFree: !course.isFree, planIds: !course.isFree ? [] : course.planIds })}
                            >
                              {course.isFree ? 'Gratuito' : 'Pago'}
                            </button>
                            {!course.isFree && plans.map((plan) => (
                              <button key={plan.id}
                                className="goals-plan"
                                style={(course.planIds || []).includes(plan.id) ? { background: plan.color || '#d17d34', color: '#fff', borderColor: 'transparent' } : undefined}
                                onClick={() => {
                                  const planIds = (course.planIds || []).includes(plan.id) ? course.planIds.filter((x) => x !== plan.id) : [...(course.planIds || []), plan.id];
                                  patchCourse(goal, i, { planIds });
                                }}>
                                {plan.name}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="goals-card-actions">
                      <button className="mg-act mg-act-amber" onClick={() => openEdit(goal)}><IconEdit /> Editar</button>
                      <button className="mg-act mg-act-red" onClick={() => handleDelete(goal)}><IconTrash /> Deletar</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {showForm && (
        <Modal title={editingId ? 'Editar meta' : 'Nova meta'} onClose={closeForm} maxWidth={560}>
          <form onSubmit={submitForm}>
            <div className="mg-field-row">
              <Field label="Tipo">
                <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="courses_per_year">Cursos lançados por ano</option>
                </Select>
              </Field>
              <Field label="Ano">
                <TextInput type="number" min="2024" max="2050" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || form.year })} />
              </Field>
            </div>
            <Field label="Meta (nº de cursos)">
              <TextInput type="number" min="1" value={form.target} onChange={(e) => setForm({ ...form, target: parseInt(e.target.value) || 1 })} />
            </Field>

            <Field label="Cursos planejados">
              <div className="goals-draft">
                <input className="mg-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDraftCourse(); } }} placeholder="Nome do curso" />
                <button type="button" className={`goals-tag${draft.isFree ? ' is-free' : ''}`} onClick={() => setDraft({ ...draft, isFree: !draft.isFree })}>
                  {draft.isFree ? 'Gratuito' : 'Pago'}
                </button>
                <button type="button" className="mg-icon-btn" onClick={addDraftCourse}><IconPlus size={16} /></button>
              </div>
              {!draft.isFree && plans.length > 0 && (
                <div className="goals-draft-plans">
                  {plans.map((plan) => (
                    <button key={plan.id} type="button" className="goals-plan"
                      style={draft.planIds.includes(plan.id) ? { background: plan.color || '#d17d34', color: '#fff', borderColor: 'transparent' } : undefined}
                      onClick={() => toggleDraftPlan(plan.id)}>{plan.name}</button>
                  ))}
                </div>
              )}
            </Field>

            {form.courses.length > 0 && (
              <div className="goals-draft-list">
                {form.courses.map((course, i) => (
                  <div key={i} className="goals-draft-item">
                    <span>{course.name}</span>
                    <span className={`goals-tag${course.isFree ? ' is-free' : ''}`} style={{ pointerEvents: 'none' }}>{course.isFree ? 'Gratuito' : 'Pago'}</span>
                    <button type="button" className="mg-icon-btn is-danger" onClick={() => setForm({ ...form, courses: form.courses.filter((_, idx) => idx !== i) })}><IconX size={13} /></button>
                  </div>
                ))}
              </div>
            )}

            <div className="mg-modal-actions">
              <button type="button" className="mg-btn mg-btn-ghost" onClick={closeForm}>Cancelar</button>
              <button type="submit" className="mg-btn mg-btn-primary">{editingId ? 'Atualizar' : 'Salvar'}</button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}
